<template>
  <v-card id="promotion-list">
    <v-card-text class="d-flex align-center flex-wrap pb-0">
      <div class="d-flex align-center pb-5">
        <v-btn
          color="primary"
          class="me-3"
          @click="syncRestaurants()"
        >
          <span>Синхронизировать</span>
        </v-btn>

        <v-btn
          color="secondary"
          outlined
          class="me-3"
          :loading="secretsExporting"
          @click="exportSecrets()"
        >
          <v-icon
            left
            size="18"
          >
            {{ icons.mdiDownload }}
          </v-icon>
          <span>Экспорт CSV</span>
        </v-btn>

        <v-btn
          color="secondary"
          outlined
          :loading="secretsImporting"
          @click="triggerImport()"
        >
          <v-icon
            left
            size="18"
          >
            {{ icons.mdiUpload }}
          </v-icon>
          <span>Импорт CSV</span>
        </v-btn>

        <input
          ref="secretsFile"
          type="file"
          accept=".csv,text/csv"
          class="d-none"
          @change="onSecretsFileSelected"
        />
      </div>

      <v-spacer></v-spacer>

      <div class="d-flex align-center pb-5">
        <v-text-field
          v-model="filters.searchQuery"
          single-line
          dense
          outlined
          hide-details
          placeholder="Поиск..."
          class="promotion-list-search me-3"
        ></v-text-field>
      </div>
    </v-card-text>

    <v-data-table
      :headers="headers"
      :items="restaurants"
      :server-items-length="totalCount"
      :loading="loading"
      :page.sync="filters.pageNumber"
      :items-per-page.sync="filters.pageSize"
      :sort-by.sync="filters.sortBy"
      :sort-desc.sync="filters.sortDesc"
      :item-class="rowClass"
      class="text-no-wrap"
    >
      <template #[`item.name`]="{item}">
        {{ item.name }}
      </template>

      <template #[`item.isOnlinePaymentReady`]="{item}">
        <v-chip
          v-if="!item.isOnlinePaymentEnabled"
          small
          label
          color="secondary"
          class="v-chip-light-bg secondary--text me-2"
        >
          Выключена
        </v-chip>
        <v-chip
          small
          label
          :color="item.isOnlinePaymentReady ? 'success' : 'error'"
          class="v-chip-light-bg"
          :class="item.isOnlinePaymentReady ? 'success--text' : 'error--text'"
        >
          {{ paymentStatus(item) }}
        </v-chip>
      </template>

      <template #[`item.actions`]="{item}">
        <div class="d-flex align-center justify-center">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="updateDialogShow(item.id)"
              >
                <v-icon size="18">
                  {{ icons.mdiPencilOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>Редактирование</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    <restaurant-form
      :label="'Конфигурация ресторана'"
      :cancellation="updateDialogCancel"
      :show="updateDialog"
      :save="updateRestaurant"
    ></restaurant-form>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="8000"
      multi-line
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  mdiPencilOutline,
  mdiDownload,
  mdiUpload,
} from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'
import restaurantApi from '@/api/restaurant'
import RestaurantForm from './restaurant-form'

export default {
  name: 'RestaurantList',
  components: { RestaurantForm },
  data: () => ({
    updateDialog: false,
    secretsExporting: false,
    secretsImporting: false,
    snackbar: { show: false, color: 'success', text: '' },
    icons: {
      mdiPencilOutline,
      mdiDownload,
      mdiUpload,
    },
  }),
  computed: {
    ...mapFormHandlers('restaurant', [
      'filters.pageNumber',
      'filters.pageSize',
      'filters.searchQuery',
      'filters.sortBy',
      'filters.sortDesc',
    ]),
    ...mapState('restaurant', {
      restaurants: 'list',
      totalCount: 'totalCount',
      loading: 'loading',
    }),
    headers() {
      return [
        { text: 'Имя', value: 'name' },
        { text: 'Онлайн-оплата', value: 'isOnlinePaymentReady' },
        {
          text: 'Действия',
          value: 'actions',
          align: 'center',
          sortable: false,
        },
      ]
    },
  },
  async created() {
    document.title = 'Рестораны'
    await this.fetchRestaurants()
  },
  mounted() {
  },
  methods: {
    ...mapActions('restaurant', {
      fetchRestaurants: 'fetchList',
      syncRestaurants: 'sync',
    }),
    ...mapActions('restaurant/form', ['resetForm', 'fetchConfigurationById', 'updateConfiguration']),
    updateDialogCancel() {
      this.updateDialog = false
    },
    updateDialogShow(id) {
      this.fetchConfigurationById(id)
      this.updateDialog = true
    },
    async updateRestaurant() {
      await this.updateConfiguration()
      this.updateDialog = false

      await this.fetchRestaurants()
    },
    notify(text, color) {
      this.snackbar = { show: true, color, text }
    },

    rowClass(item) {
      if (!item.isOnlinePaymentReady) return 'restaurant-row--incomplete'

      return item.isOnlinePaymentEnabled ? 'restaurant-row--ready' : ''
    },

    paymentStatus(item) {
      if (item.isOnlinePaymentReady) {
        return 'Настроен на онлайн-оплату'
      }

      const missing = [
        [item.hasQrAuthToken, 'токен QR-кода'],
        [item.hasPayLinkAuthToken, 'токен платёжной ссылки'],
        [item.hasAccountNo, 'номер счёта'],
      ].filter(([filled]) => !filled).map(([, name]) => name)

      return `Не заполнено: ${missing.join(', ')}`
    },

    async fetchAllRestaurants() {
      const { data } = await restaurantApi.getAll({ pageNumber: 1, pageSize: 100000 })

      return data.items || []
    },

    csvCell(value) {
      const text = value === null || value === undefined ? '' : String(value)
      if (/[";\r\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`
      }

      return text
    },

    parseCsv(text) {
      const clean = text.replace(/^\uFEFF/, '')
      const rows = []
      let row = []
      let field = ''
      let inQuotes = false
      for (let i = 0; i < clean.length; i += 1) {
        const c = clean[i]
        if (inQuotes) {
          if (c === '"') {
            if (clean[i + 1] === '"') { field += '"'; i += 1 } else { inQuotes = false }
          } else { field += c }
        } else if (c === '"') { inQuotes = true } else if (c === ';') { row.push(field); field = '' } else if (c === '\n') {
          row.push(field); rows.push(row); row = []; field = ''
        } else if (c !== '\r') { field += c }
      }
      if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row) }

      return rows
    },

    async exportSecrets() {
      this.secretsExporting = true
      try {
        const restaurants = await this.fetchAllRestaurants()
        const rows = [['DeliveryRestaurantId', 'Account', 'TokenUrl', 'TokenQR']]

        // eslint-disable-next-line no-restricted-syntax
        for (const r of restaurants) {
          // eslint-disable-next-line no-await-in-loop
          const { data: s } = await restaurantApi.getSecretsById(r.id)
          rows.push([r.id, s.accountNo || '', s.payLinkAuthToken || '', s.qrAuthToken || ''])
        }
        const csv = rows.map(cols => cols.map(this.csvCell).join(';')).join('\r\n')
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'restaurant-secrets.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.notify('Не удалось выгрузить CSV', 'error')
      } finally {
        this.secretsExporting = false
      }
    },
    triggerImport() {
      this.$refs.secretsFile.value = null
      this.$refs.secretsFile.click()
    },

    async onSecretsFileSelected(event) {
      const [file] = event.target.files || []
      if (!file) {
        return
      }

      this.secretsImporting = true
      try {
        const text = await file.text()
        const rows = this.parseCsv(text)
        const restaurants = await this.fetchAllRestaurants()
        const ids = new Set(restaurants.map(r => String(r.id)))

        let total = 0
        let updated = 0
        const notFound = []
        const errors = []

        // eslint-disable-next-line no-restricted-syntax
        for (const cols of rows) {
          const id = (cols[0] || '').trim()

          if (/^\d+$/.test(id)) {
            total += 1
            if (ids.has(id)) {
              const payload = { id: Number(id) }
              const account = (cols[1] || '').trim()
              const tokenUrl = (cols[2] || '').trim()
              const tokenQr = (cols[3] || '').trim()
              if (account) payload.accountNo = account
              if (tokenQr) payload.qrAuthToken = tokenQr
              if (tokenUrl) payload.payLinkAuthToken = tokenUrl

              try {
                // eslint-disable-next-line no-await-in-loop
                await restaurantApi.updateConfiguration(payload)
                updated += 1
              } catch (e) {
                errors.push(id)
              }
            } else {
              notFound.push(id)
            }
          }
        }

        const parts = [`Обновлено: ${updated} из ${total}`]
        if (notFound.length) parts.push(`не найдены: ${notFound.join(', ')}`)
        if (errors.length) parts.push(`ошибки: ${errors.join(', ')}`)
        this.notify(parts.join('; '), errors.length ? 'warning' : 'success')
        await this.fetchRestaurants()
      } catch (e) {
        this.notify('Не удалось импортировать CSV', 'error')
      } finally {
        this.secretsImporting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#promotion-list {
  .promotion-list-search {
    max-width: 10.625rem;
  }

  ::v-deep tbody {
    tr.restaurant-row--incomplete > td {
      background-color: rgba(255, 76, 81, 0.08);
    }

    tr.restaurant-row--incomplete:hover > td {
      background-color: rgba(255, 76, 81, 0.14);
    }

    tr.restaurant-row--ready > td {
      background-color: rgba(86, 202, 0, 0.08);
    }

    tr.restaurant-row--ready:hover > td {
      background-color: rgba(86, 202, 0, 0.14);
    }
  }
}
</style>
